import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "@/components/common/mailbox-editor.css";
import { Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import { useSendMessage } from "@/hooks/inbox/useMessages";
import { useDraft, getDraft } from "@/hooks/inbox/useDraft";
import { getChannel, isEmptyRichText } from "./utils";
import type { InboxConversation } from "@/types/inbox";

// Quill toolbar markup (Quill binds to the container by id).
const EmailToolbar: React.FC<{ toolbarId: string }> = ({ toolbarId }) => (
	<div id={toolbarId} className="flex w-full items-center border-b border-border bg-muted/50 px-2 py-1">
		<div className="flex items-center gap-1">
			<select className="ql-header" defaultValue="">
				<option value="1"></option>
				<option value="2"></option>
				<option value=""></option>
			</select>
			<button className="ql-bold" aria-label="Bold" />
			<button className="ql-italic" aria-label="Italic" />
			<button className="ql-underline" aria-label="Underline" />
			<button className="ql-list" value="ordered" aria-label="Ordered list" />
			<button className="ql-list" value="bullet" aria-label="Bullet list" />
			<button className="ql-link" aria-label="Link" />
			<button className="ql-clean" aria-label="Clear formatting" />
		</div>
	</div>
);

interface ComposerProps {
	conversation: InboxConversation;
	/** Unique per mounted surface — Quill needs a distinct toolbar element id. */
	toolbarId: string;
}

/**
 * Dual-mode composer: rich-text email editor with an explicit Send action,
 * or an auto-growing chat input where Enter sends and Shift+Enter breaks.
 * Drafts persist per conversation and are restored after failed sends.
 */
export const Composer: React.FC<ComposerProps> = ({ conversation, toolbarId }) => {
	const { t } = useTranslation();
	const isEmail = getChannel(conversation) === "email";
	const [value, setValue] = useState("");
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const { saveDraft, flushDraft, clearDraft } = useDraft(conversation.id);
	const sendMutation = useSendMessage(conversation);

	// Restore the saved draft when switching conversations.
	useEffect(() => {
		setValue(getDraft(conversation.id));
	}, [conversation.id]);

	const handleChange = useCallback(
		(next: string) => {
			setValue(next);
			saveDraft(next);
		},
		[saveDraft]
	);

	const canSend = isEmail ? !isEmptyRichText(value) : !!value.trim();

	const handleSend = useCallback(() => {
		if (!canSend || sendMutation.isPending) return;
		const text = isEmail ? value.trim() : value.trim();
		setValue("");
		clearDraft();
		sendMutation.mutate(text, {
			onError: (error) => {
				// Give the user their message back to retry.
				setValue(text);
				flushDraft(text);
				toast({
					title: t("inbox.sendFailedTitle", "Message not sent"),
					description:
						error instanceof Error && error.message
							? error.message
							: t("inbox.sendFailedDescription", "Something went wrong. Your draft was restored."),
					variant: "destructive",
				});
			},
		});
	}, [canSend, sendMutation, isEmail, value, clearDraft, flushDraft, t]);

	const handleTextareaKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (event.key === "Enter" && !event.shiftKey) {
			event.preventDefault();
			handleSend();
		}
	};

	// Auto-grow the chat textarea up to ~6 lines.
	useEffect(() => {
		const textarea = textareaRef.current;
		if (!textarea) return;
		textarea.style.height = "auto";
		textarea.style.height = `${Math.min(textarea.scrollHeight, 144)}px`;
	}, [value, isEmail]);

	if (isEmail) {
		return (
			<div className="shrink-0 border-t border-border bg-background p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
				<div className="overflow-hidden rounded-sm border border-border bg-card mailbox-email-editor">
					<EmailToolbar toolbarId={toolbarId} />
					<ReactQuill
						value={value}
						onChange={handleChange}
						modules={{ toolbar: { container: `#${toolbarId}` } }}
						theme="snow"
						placeholder={t("inbox.composer.emailPlaceholder", "Write your reply…")}
						readOnly={sendMutation.isPending}
					/>
				</div>
				<div className="mt-2 flex items-center justify-end gap-3">
					<Button type="button" size="sm" className="gap-2" disabled={!canSend || sendMutation.isPending} onClick={handleSend}>
						{sendMutation.isPending ? (
							<Loader2 className="h-4 w-4 animate-spin" />
						) : (
							<Send className="h-4 w-4" />
						)}
						{sendMutation.isPending
							? t("inbox.composer.sending", "Sending…")
							: t("inbox.composer.send", "Send")}
					</Button>
				</div>
			</div>
		);
	}

	return (
		<form
			className="flex shrink-0 items-end gap-2 border-t border-border bg-background p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
			onSubmit={(event) => {
				event.preventDefault();
				handleSend();
			}}
		>
			<Textarea
				ref={textareaRef}
				value={value}
				onChange={(event) => handleChange(event.target.value)}
				onKeyDown={handleTextareaKeyDown}
				placeholder={t("inbox.composer.chatPlaceholder", "Write a message…")}
				rows={1}
				className="max-h-36 min-h-9 flex-1 resize-none py-2"
				aria-label={t("inbox.composer.chatPlaceholder", "Write a message…")}
			/>
			<Button
				type="submit"
				size="icon"
				className="h-9 w-9 shrink-0"
				disabled={!canSend || sendMutation.isPending}
				aria-label={t("inbox.composer.send", "Send")}
			>
				{sendMutation.isPending ? (
					<Loader2 className="h-4 w-4 animate-spin" />
				) : (
					<Send className="h-4 w-4" />
				)}
			</Button>
		</form>
	);
};
